import { I18n } from "@assets/strings";
import React, { useEffect, useRef, useState } from "react";
import AvatarEditor, { AvatarEditorRef } from "react-avatar-editor";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import defaultProfilePicture from "@assets/images/variant-jaangal/defaults/profile-picture.svg";
import { useServices } from "@core/servicesContext";
import { CurrentProfile } from "@domain/currentProfile/currentProfile";
import { Filter } from "@domain/filter/filter";
import { useMyFilters } from "@domain/filter/hooks";
import { ImageVersions } from "@domain/media/dtos";
import { useSource } from "@domain/media/hooks";
import { Media } from "@domain/media/media";
import { combineAvatarWithFilter, dataURLToFile, handleUploadFileError } from "@utils/file";
import { useAsync, useIsMobile } from "@utils/hooks";
import { ClosableModal, ModalTitle } from "@ui/modals/closableModal";
import { desktop, mobile } from "@ui/styles/utils";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { SecondaryButton } from "@ui/components/buttons/secondaryButton";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { FileInput } from "@ui/components/inputs/fileInput";
import { RangePicker } from "@ui/components/inputs/rangePicker";
import { row, Stack, stack } from "@ui/components/layout";
import { ImageView } from "@ui/components/media/imageView";
import { TabView } from "@ui/components/tabViews";

interface CropProfilePictureProps extends React.HTMLAttributes<HTMLDivElement> {
	close: () => void;
	initialProfile: CurrentProfile;
}

const TABS = ["NEW_PROFILE", "NEW_FILTER"] as const;
type tab = (typeof TABS)[number];

export const CropProfilePicture: React.FC<CropProfilePictureProps> = ({ close, initialProfile, ...htmlProps }) => {
	const { avatarOriginal } = initialProfile;
	const [profilePictute, setProfilePicture] = useState<string | File | undefined>(undefined);
	const [originalProfilePictute, setOriginalProfilePicture] = useState<File | null>(null);
	const [newProfile, setNewProfile] = useState(initialProfile);
	const [avatarCrop, setAvatarCrop] = useState(initialProfile?.avatarCrop);
	const { sourceLoaded, loading: loaded } = useSource(avatarOriginal?.url);

	useEffect(() => {
		if (sourceLoaded) {
			// we add this concat to tell chrome dot not fecth image from cache
			// else if we gonna got some cors error.
			// https://www.hacksoft.io/blog/handle-images-cors-error-in-chrome
			setProfilePicture(sourceLoaded.concat("?", "notFromCache=true"));
		}
	}, [sourceLoaded]);

	const avatarEditorRef = useRef<AvatarEditorRef>(null);
	const isMobile = useIsMobile();

	const [mimeType, setMimeType] = useState(avatarOriginal?.mimeType ?? "image/png");
	const [zoom, setZoom] = useState(1);
	const [tabIndex, setTabIndex] = useState(0);
	const [tab, setTab] = useState<tab>("NEW_PROFILE");
	const [loading, setLoading] = useState(false);
	const [selectedFilter, setFilter] = useState<Filter | null>(null);

	const { mediaService, currentProfileService } = useServices();
	const { run } = useAsync(
		(newProfile: CurrentProfile) => currentProfileService.saveProfileInfos(newProfile),
		[initialProfile]
	);

	const { result: filters } = useMyFilters();

	//TODO change librairie comportement to have a good UX when image is processing.
	//https://github.com/mosch/react-avatar-editor/issues/385.

	return (
		<Container {...htmlProps} keyboardEventDisabled isCorpProfilePicture close={close}>
			<ModalTitle>{I18n.t("profile.edit.crop.avatar.title")}</ModalTitle>
			{tab === "NEW_PROFILE" ? (
				<ContainerCrop>
					{loaded ? (
						<ClipLoader color="#fff" />
					) : (
						<AvatarEditor
							image={profilePictute ?? defaultProfilePicture}
							ref={avatarEditorRef}
							crossOrigin="anonymous"
							width={250}
							height={250}
							borderRadius={150}
							color={[255, 255, 255, 0.6]}
							scale={zoom}
						/>
					)}
				</ContainerCrop>
			) : tab === "NEW_FILTER" ? (
				<>
					{avatarCrop ? (
						<ContainerCrop>
							<StyledImageView image={avatarCrop} imageVersion={ImageVersions.LARGE} zoomDisabled={true} />
							{selectedFilter && (
								<StyledFilter image={selectedFilter.asset} imageVersion={ImageVersions.LARGE} zoomDisabled={true} />
							)}
						</ContainerCrop>
					) : (
						<h6>{I18n.t("profile.edit.crop.error_message", { size: 250 })}</h6>
					)}
				</>
			) : null}
			<TabView
				style={{ width: "100%" }}
				items={TABS}
				labelKey="profile.card.edit"
				tabIndex={tabIndex}
				onClick={(index, tab) => {
					setTabIndex(index);
					setTab(tab);
				}}
			/>
			{tab === "NEW_PROFILE" ? (
				<Stack gap={30} align="center" style={{ width: "100%" }}>
					<Range
						disabled={!profilePictute}
						value={zoom}
						step={0.1}
						min={1}
						max={3}
						onChange={(value) => setZoom(value)}
					/>
					{loading ? (
						<ClipLoader />
					) : (
						<Actions>
							<FileInput
								multiple={false}
								type="image"
								onChange={(file) => {
									if (file) {
										setMimeType(file[0].type);
										setProfilePicture(file[0]);
										setOriginalProfilePicture(file[0]);
										setAvatarCrop(undefined);
									}
								}}
							>
								{(dragging) =>
									dragging ? <ClipLoader /> : <SecondaryButton onClick={() => close()}>{I18n.t("add")}</SecondaryButton>
								}
							</FileInput>
							<LoadingButton
								disabled={!profilePictute}
								onClick={async () => {
									if (avatarEditorRef.current) {
										const dataURL = avatarEditorRef.current.getImageScaledToCanvas().toDataURL(mimeType);
										try {
											setLoading(true);
											let avatarOriginal: Media | undefined;
											let newProfile: CurrentProfile;
											const avatarCrop = await mediaService.uploadMedia(dataURLToFile(dataURL, mimeType.split("/")[1]));
											setAvatarCrop(avatarCrop);
											if (originalProfilePictute) {
												avatarOriginal = await mediaService.uploadMedia(originalProfilePictute);
												newProfile = await run({ ...initialProfile, avatarOriginal, avatarCrop, avatar: avatarCrop });
											} else {
												newProfile = await run({ ...initialProfile, avatarCrop, avatar: avatarCrop });
											}
											setNewProfile(newProfile);
											setTabIndex(1);
											setTab("NEW_FILTER");
										} catch (e) {
											handleUploadFileError(e as Error);
										} finally {
											setLoading(false);
										}
									}
								}}
							>
								{I18n.t("validate")}
							</LoadingButton>
						</Actions>
					)}
				</Stack>
			) : tab === "NEW_FILTER" ? (
				<>
					{avatarCrop ? (
						<Stack gap={25} align="center" style={{ width: "100%" }}>
							{filters && (
								<Blocs>
									<FilterInfo onClick={() => setFilter(null)} selected={selectedFilter === null}>
										<FilterBloc>
											<ProfileView
												image={avatarCrop}
												imageVersion={isMobile ? ImageVersions.SMALL : ImageVersions.LARGE}
												zoomDisabled={true}
											/>
										</FilterBloc>
										<h6>{I18n.t("profile.edit.crop.filter.no_filter")}</h6>
									</FilterInfo>
									{filters.map((filter) => (
										<FilterInfo
											selected={filter.id === selectedFilter?.id}
											key={filter.id}
											onClick={() => setFilter(filter)}
										>
											<FilterBloc>
												<ProfileView
													image={avatarCrop}
													imageVersion={isMobile ? ImageVersions.SMALL : ImageVersions.LARGE}
													zoomDisabled={true}
												/>
												<FilterView
													image={filter.asset}
													imageVersion={isMobile ? ImageVersions.SMALL : ImageVersions.LARGE}
													zoomDisabled={true}
												/>
											</FilterBloc>
											<h6>{"#".concat("", filter.filterName)}</h6>
										</FilterInfo>
									))}
								</Blocs>
							)}
							{loading ? (
								<ClipLoader />
							) : (
								<Actions>
									<LoadingButton
										onClick={async () => {
											try {
												setLoading(true);
												if (!selectedFilter) {
													await run({ ...newProfile, avatar: avatarCrop, filter: undefined });
												} else {
													const newPicture = await combineAvatarWithFilter(avatarCrop, selectedFilter.asset);
													const media = await mediaService.uploadMedia(newPicture);
													await run({ ...newProfile, avatar: media, filter: selectedFilter });
												}
												close();
											} catch (e) {
												handleUploadFileError(e as Error);
											} finally {
												setLoading(false);
											}
										}}
									>
										{I18n.t("validate")}
									</LoadingButton>
								</Actions>
							)}
						</Stack>
					) : null}
				</>
			) : null}
		</Container>
	);
};

const Container = styled(ClosableModal)`
	${stack(30)};
`;

const ContainerCrop = styled.div`
	${stack(0, "center", "center")}
	width: 100%;
	height: 370px;
	margin-top: 20px;
	background-color: ${({ theme }) => theme.colors.black};
	position: relative;
`;

const Actions = styled.div`
	${row(10, "space-around", "center")}

	@media ${desktop} {
		gap: 45px;
	}
`;

const StyledImageView = styled(ImageView)`
	> img {
		border-radius: 50%;
	}
`;

const StyledFilter = styled(ImageView)`
	position: absolute;
	width: 350px;
	> img {
		border-radius: 50%;
	}
`;

const Blocs = styled.div`
	width: 100%;
	${row(5, "space-between")}
`;

const FilterInfo = styled(RoundedBlock)<{ selected?: boolean }>`
	${stack(5, "center", "center")};
	padding: 5px 10px;
	cursor: pointer;
	${({ selected, theme }) => selected && `border:1px solid ${theme.colors.secondary}`}
`;

const FilterBloc = styled.div`
	position: relative;
	${row(0, "center", "center")}
	width: 160px;
	height: 160px;

	@media ${mobile} {
		width: 70px;
		height: 90px;
	}
`;

const FilterView = styled(ImageView)`
	position: absolute;
	width: 224px;
	z-index: 9;

	@media ${mobile} {
		width: 100px;
	}
`;

const ProfileView = styled(ImageView)`
	position: absolute;
	> img {
		border-radius: 50%;
	}
`;

const Range = styled(RangePicker)`
	width: 50%;
	@media ${mobile} {
		width: 100%;
	}
`;
